// Kho concept + trang phục (bảng concepts/costumes, QĐ 0096) — thay 5 khoá settings cũ
// (custom_concepts, concept_data_v2, concept_branch_scopes, custom_costumes, costume_images).
// Mọi hook đọc qua openapi-fetch; mutation tự vô hiệu cache theo khoá gốc.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { CATALOG_KEY } from "@/features/products/catalogApi";

export type LookbookConcept = components["schemas"]["LookbookConcept"];
export type LookbookConceptInput = components["schemas"]["LookbookConceptInput"];
export type LookbookConceptUpdate = components["schemas"]["LookbookConceptUpdate"];
export type LookbookCostume = components["schemas"]["LookbookCostume"];
export type LookbookCostumeInput = components["schemas"]["LookbookCostumeInput"];
export type LookbookCostumeUpdate = components["schemas"]["LookbookCostumeUpdate"];
export type LookbookImage = components["schemas"]["LookbookImage"];

export const LOOKBOOK_KEY = ["lookbook"] as const;
const conceptsKey = (brand: string | undefined, includeInactive: boolean) =>
  [...LOOKBOOK_KEY, "concepts", brand ?? "all", includeInactive] as const;
const costumesKey = (includeRetired: boolean) =>
  [...LOOKBOOK_KEY, "costumes", includeRetired] as const;

// Câu máy chủ nói rõ vì sao ("mỗi concept tối đa 12 ảnh — xoá một ảnh trước
// khi thêm"); đọc phẳng thì luôn ra câu dự phòng vì lời nằm trong error.error.
function beMessage(error: unknown): string {
  return serverErrorMessage(error, "Máy chủ từ chối yêu cầu");
}

/** Danh sách concept của một thương hiệu (bỏ trống = mọi thương hiệu). */
export function useLookbookConcepts(brand?: string, includeInactive = false) {
  return useQuery({
    queryKey: conceptsKey(brand, includeInactive),
    queryFn: async () => {
      const { data, error } = await api.GET("/concepts", {
        params: {
          query: {
            ...(brand ? { brand } : {}),
            ...(includeInactive ? { includeInactive: true } : {}),
          },
        },
      });
      if (error) throw new Error(beMessage(error));
      return data?.items ?? [];
    },
    staleTime: 60_000,
  });
}

export function useLookbookConcept(id: string | undefined) {
  return useQuery({
    queryKey: [...LOOKBOOK_KEY, "concept", id ?? ""],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await api.GET("/concepts/{id}", {
        params: { path: { id: id ?? "" } },
      });
      if (error) throw new Error(beMessage(error));
      return data ?? null;
    },
  });
}

export function useLookbookCostumes(includeRetired = false) {
  return useQuery({
    queryKey: costumesKey(includeRetired),
    queryFn: async () => {
      const { data, error } = await api.GET("/costumes", {
        params: { query: includeRetired ? { includeRetired: true } : {} },
      });
      if (error) throw new Error(beMessage(error));
      return data?.items ?? [];
    },
    staleTime: 60_000,
  });
}

function useInvalidateLookbook() {
  const qc = useQueryClient();
  // Ảnh #1 của concept kiểu gói (Chụp Ảnh) cũng là ảnh đại diện gói
  // (products.image_url, QĐ 0111) — máy chủ soi sang, nên danh mục gói phải
  // đọc lại kẻo thẻ gói / hộp Sửa gói còn ảnh cũ.
  return () => {
    void qc.invalidateQueries({ queryKey: LOOKBOOK_KEY });
    void qc.invalidateQueries({ queryKey: CATALOG_KEY });
  };
}

export function useCreateConcept() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async (body: LookbookConceptInput) => {
      const { data, error } = await api.POST("/concepts", { body });
      if (error) throw new Error(beMessage(error));
      return data as LookbookConcept;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateConcept() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: LookbookConceptUpdate }) => {
      const { data, error } = await api.PATCH("/concepts/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookConcept;
    },
    onSuccess: invalidate,
  });
}

export function useSetConceptActive() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { data, error } = await api.PATCH("/concepts/{id}/active", {
        params: { path: { id } },
        body: { isActive },
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookConcept;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteConcept() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/concepts/{id}", { params: { path: { id } } });
      if (error) throw new Error(beMessage(error));
    },
    onSuccess: invalidate,
  });
}

export function useAddConceptImage() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, url }: { id: string; url: string }) => {
      const { data, error } = await api.POST("/concepts/{id}/images", {
        params: { path: { id } },
        body: { url },
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookConcept;
    },
    onSuccess: invalidate,
  });
}

export function useReorderConceptImages() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, imageIds }: { id: string; imageIds: number[] }) => {
      const { data, error } = await api.PUT("/concepts/{id}/images/order", {
        params: { path: { id } },
        body: { imageIds },
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookConcept;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteConceptImage() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, imageId }: { id: string; imageId: number }) => {
      const { data, error } = await api.DELETE("/concepts/{id}/images/{imageId}", {
        params: { path: { id, imageId } },
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookConcept;
    },
    onSuccess: invalidate,
  });
}

export function useCreateCostume() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async (body: LookbookCostumeInput) => {
      const { data, error } = await api.POST("/costumes", { body });
      if (error) throw new Error(beMessage(error));
      return data as LookbookCostume;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateCostume() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: LookbookCostumeUpdate }) => {
      const { data, error } = await api.PATCH("/costumes/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookCostume;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteCostume() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/costumes/{id}", { params: { path: { id } } });
      if (error) throw new Error(beMessage(error));
    },
    onSuccess: invalidate,
  });
}

export function useAddCostumeImage() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, url }: { id: string; url: string }) => {
      const { data, error } = await api.POST("/costumes/{id}/images", {
        params: { path: { id } },
        body: { url },
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookCostume;
    },
    onSuccess: invalidate,
  });
}

export function useReorderCostumeImages() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, imageIds }: { id: string; imageIds: number[] }) => {
      const { data, error } = await api.PUT("/costumes/{id}/images/order", {
        params: { path: { id } },
        body: { imageIds },
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookCostume;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteCostumeImage() {
  const invalidate = useInvalidateLookbook();
  return useMutation({
    mutationFn: async ({ id, imageId }: { id: string; imageId: number }) => {
      const { data, error } = await api.DELETE("/costumes/{id}/images/{imageId}", {
        params: { path: { id, imageId } },
      });
      if (error) throw new Error(beMessage(error));
      return data as LookbookCostume;
    },
    onSuccess: invalidate,
  });
}
